import React, { useEffect, useState } from 'react';
import { Modal, Table, Form, Select, InputNumber, Button, Space, message, Empty, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { malzemeBirimDonusumleriAPI } from '../services/api';
import { formatSayi } from '../utils/format';
import dayjs from 'dayjs';

function BirimDonusumModal({ open, malzeme, birimler, birimlerRaw, onClose, onSaved }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchData = async () => {
    if (!malzeme?.id) return;
    setLoading(true);
    try {
      const res = await malzemeBirimDonusumleriAPI.getByMalzeme(malzeme.id);
      setData(res.data);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && malzeme?.id) {
      fetchData();
      form.resetFields();
    }
  }, [open, malzeme?.id]);

  // Malzemenin birim grubu (kg/gr veya lt/ml)
  const idByAd = Object.fromEntries((birimlerRaw || []).map(b => [b.ad, b.id]));
  const adById = Object.fromEntries((birimlerRaw || []).map(b => [b.id, b.ad]));
  const donusumGruplari = [
    [idByAd['kg'], idByAd['gr']].filter(Boolean),
    [idByAd['lt'], idByAd['ml']].filter(Boolean),
  ];
  const malzemeBirimId = malzeme?.birim_id;
  const malzemeBirimGrup = donusumGruplari.find(g => g.includes(malzemeBirimId)) || [malzemeBirimId];

  // Her iki dropdown'da tüm birimler; validasyon: taraflardan biri malzeme grubunda olmalı
  const kaynakBirimOptions = birimler;
  const hedefBirimOptions = birimler;

  const handleSubmit = async (values) => {
    let { kaynak_birim_id, hedef_birim_id, carpan } = values;
    const kaynakGrupta = malzemeBirimGrup.includes(kaynak_birim_id);
    const hedefGrupta = malzemeBirimGrup.includes(hedef_birim_id);
    if (kaynakGrupta === hedefGrupta) {
      message.error('Dönüşümün bir tarafı malzemenin birimi (ya da eş grubu) olmalı, diğer taraf farklı bir birim olmalı.');
      return;
    }
    // Kanonik biçim: hedef malzeme grubunda olmalı. Ters yönde girilmişse çevir.
    if (kaynakGrupta && !hedefGrupta) {
      [kaynak_birim_id, hedef_birim_id] = [hedef_birim_id, kaynak_birim_id];
      carpan = 1 / Number(carpan);
      message.info(`Kaydedildi: 1 ${adById[kaynak_birim_id]} = ${formatSayi(carpan, 2)} ${adById[hedef_birim_id]}`);
    }
    try {
      await malzemeBirimDonusumleriAPI.create({
        malzeme_id: malzeme.id,
        kaynak_birim_id,
        hedef_birim_id,
        carpan,
      });
      message.success('Dönüşüm eklendi');
      form.resetFields();
      fetchData();
      if (onSaved) onSaved();
    } catch (err) {
      message.error('Dönüşüm eklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  // Son geçerli dönüşümü vurgula
  const sonDonusumMap = {};
  data.forEach(d => {
    const key = `${d.kaynak_birim_id}-${d.hedef_birim_id}`;
    if (!sonDonusumMap[key] || new Date(d.tarih) > new Date(sonDonusumMap[key].tarih)) {
      sonDonusumMap[key] = d;
    }
  });
  const sonIds = new Set(Object.values(sonDonusumMap).map(d => d.id));

  const columns = [
    {
      title: 'Kaynak',
      dataIndex: 'kaynak_birim',
      key: 'kaynak_birim',
      width: 80,
    },
    {
      title: 'Dönüşüm',
      key: 'donusum',
      render: (_, r) => `1 ${r.kaynak_birim} = ${formatSayi(r.carpan, 2)} ${r.hedef_birim}`,
    },
    {
      title: 'Tarih',
      dataIndex: 'tarih',
      key: 'tarih',
      width: 140,
      render: (v) => v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: 'Durum',
      key: 'durum',
      width: 80,
      render: (_, r) => sonIds.has(r.id) ? <Tag color="green">Aktif</Tag> : <Tag>Eski</Tag>,
    },
  ];

  return (
    <Modal
      title={malzeme ? `${malzeme.ad} — Birim Dönüşümleri` : 'Birim Dönüşümleri'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
      zIndex={1100}
    >
      <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
        <div style={{ marginBottom: 4, fontWeight: 500 }}>Yeni Dönüşüm Ekle</div>
        <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>Taraflardan biri malzemenin birimi (veya eş grubu — kg/gr, lt/ml) olmalı. İstediğiniz yönde girebilirsiniz, ters girilirse sistem kanonik biçime çevirir.</div>
        <Form form={form} layout="inline" onFinish={handleSubmit}>
          <span style={{ lineHeight: '32px', marginRight: 4 }}>1</span>
          <Form.Item name="kaynak_birim_id" rules={[{ required: true, message: 'Seçin' }]}>
            <Select placeholder="Birim" options={kaynakBirimOptions} style={{ width: 100 }} />
          </Form.Item>
          <span style={{ lineHeight: '32px', margin: '0 4px' }}>=</span>
          <Form.Item name="carpan" rules={[{ required: true, message: 'Girin' }]}>
            <InputNumber placeholder="Miktar" min={0.0001} step={0.1} style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name="hedef_birim_id" rules={[{ required: true, message: 'Seçin' }]} initialValue={malzemeBirimGrup[malzemeBirimGrup.length - 1]}>
            <Select placeholder="Birim" options={hedefBirimOptions} style={{ width: 80 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>Ekle</Button>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
        locale={{ emptyText: <Empty description="Henüz dönüşüm tanımlanmamış" /> }}
      />
    </Modal>
  );
}

export default BirimDonusumModal;
